import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Box, Moon, Sun, LogOut, Images, History as HistoryIcon, Upload } from "lucide-react";

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("pv-theme") === "dark");
  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("pv-theme", dark ? "dark" : "light");
  }, [dark]);
  return { dark, toggle: () => setDark((d) => !d) };
}

const navItems = [
  { to: "/", label: "Upload", icon: Upload },
  { to: "/gallery", label: "Gallery", icon: Images },
  { to: "/history", label: "My History", icon: HistoryIcon },
];

export default function Header() {
  const { user, login, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
          <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center transition-transform group-hover:-rotate-6">
            <Box className="h-5 w-5" strokeWidth={2.25} />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">ImageLink</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-medium transition-colors ${
                  active ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
            {dark ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring" data-testid="profile-menu-trigger">
                  <Avatar className="h-9 w-9 border border-border">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback>{(user.name || "U").charAt(0)}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs text-muted-foreground font-normal truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/history" data-testid="menu-history"><HistoryIcon className="h-4 w-4 mr-2" />My History</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={login} data-testid="google-signin-btn" className="rounded-full font-semibold">
              Sign in with Google
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
